import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import classes from './PspView.css';
import { startCase, toLower} from "lodash"
import Box from '../../../hoc/Box/Box';
import ReactPaginate from 'react-paginate';
import withErrorHandler from '../../../hoc/withErrorHandler/withErrorHandler';
import api from '../../../axios-local-api';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { FaEllipsisV, FaUserCheck, FaUserXmark, FaArrowUpRightFromSquare, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { FaHouseSignal } from "react-icons/fa6";
import Confirm from '../../../components/UI/Confirm/Confirm';
import Modal from '../../../components/UI/Modal/Modal';
import Spinner from '../../../components/UI/Spinner/Spinner';
import * as actions from '../../../store/actions';
import { history } from '../../..';
import ErrorAlert from '../../../shared/Alert/ErrorAlert';
import Error from '../../../components/UI/Error/Error';
import Tooltip from 'react-tooltip-lite';
import SelectDropdown from '../../../shared/SelectDropdown/SelectDropdown';


const buildDynamicOptions = (data, key, labelFn) => {
  const defaultOption = { value: "", label: "Select Status" };
  if (!data || !data.length) return [defaultOption];
  const unique = [...new Set(data.map(item => key(item)).filter(Boolean))];
  return [defaultOption, ...unique.map(val => ({ value: val, label: labelFn ? labelFn(val) : formatStatus(val) }))];
}


const formatStatus = (status) => {
  if (!status) return "N/A";
  return status.split(" ").map((word)=> {
  return /[a-zA-Z]/.test(word) ? startCase(toLower(word)) : word;
  }).join(" ")
};

const userXMark = <svg xmlns="http://www.w3.org/2000/svg" stroke='currentColor' fill='currentColor' style={{ marginLeft: '-7px' }} viewBox="0 0 640 512" height={'1em'} width={'1em'}>
  <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3zM471 143c9.4-9.4 24.6-9.4 33.9 0l47 47 47-47c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-47 47 47 47c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-47-47-47 47c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l47-47-47-47c-9.4-9.4-9.4-24.6 0-33.9z" />
</svg>

const parseQueryString = function () {
  const str = window.location.search;
  const objURL = {};

  str.replace(
    new RegExp('([^?=&]+)(=([^&]*))?', 'g'),
    ($0, $1, $2, $3) => objURL[$1] = $3
  );
  return objURL;
};

const PspView = (props) => {

  const params = useCallback(parseQueryString());
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDropDown, setShowDropDown] = useState(false);
  const [result, setResult] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState({});
  const dropDownRef = useRef();
  const [currDetails, setCurrDetails] = useState([]);
  const [searchValue, setSearchValue] = useState((props.filter && props.filter.search) || '');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth
  });
  const [filterOptions, setFilterOptions] = useState({
    address: "",
    occupancyStatus: "",
    routerStatus: "",
    iotNetwork: "",
    residentNetwork: ""
  });

  // WIN-1603: Derive dropdown options dynamically from actual data
  const occupancyOptions = useMemo(() => {
    return buildDynamicOptions(currDetails, item => item?.occupancyStatus);
  }, [currDetails]);

  const routerStatusOptions = useMemo(() => {
    return buildDynamicOptions(currDetails, item => item?.connectionStatusDropdown?.value);
  }, [currDetails]);

  const iotNetworkOptions = useMemo(() => {
    return buildDynamicOptions(currDetails, item => item?.iotNetworkStatusDropdown?.value);
  }, [currDetails]);

  const residentNetworkOptions = useMemo(() => {
    return buildDynamicOptions(currDetails, item => item?.residentNetworkStatusDropdown?.value);
  }, [currDetails]);

  useEffect(() => {
    const mappedData = mapTenantData(props.tenantDetails.content);

    if(props?.tenantDetails?.isSpectrumReady){
      sessionStorage.setItem("isSpectrumReady", true)
    }
    setCurrDetails(mappedData);
    setFilteredData(mappedData);
  }, [props.tenantDetails.content]);

  // WIN-1603: All filtering is now purely client-side
  useEffect(() => {
    const copyData = currDetails?.length ? currDetails : mapTenantData(props.tenantDetails?.content);
    const hasFilter = searchValue?.trim() || filterOptions?.occupancyStatus ||
      filterOptions.iotNetwork || filterOptions.residentNetwork ||
      filterOptions.routerStatus;

    if (hasFilter) {
      const filteredCopyData = copyData?.filter((item) => {
        return (
          (searchValue?.trim() ? item?.address?.toLowerCase()?.includes(searchValue?.toLowerCase()) : true) &&
          (filterOptions?.occupancyStatus ? item?.occupancyStatus === filterOptions?.occupancyStatus : true) &&
          (filterOptions?.iotNetwork ? item?.iotNetworkStatusDropdown?.value === filterOptions?.iotNetwork : true) &&
          (filterOptions?.residentNetwork ? item?.residentNetworkStatusDropdown?.value === filterOptions?.residentNetwork : true) &&
          (filterOptions?.routerStatus ? item?.connectionStatusDropdown?.value === filterOptions?.routerStatus : true)
        )
      });
      setFilteredData(filteredCopyData);
    } else {
      setFilteredData(copyData);
    }
    // Reset to first page whenever filters change
    setCurrentPage(0);
  }, [filterOptions, searchValue, currDetails]);

  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    window.addEventListener('unload', handleClick(0), false);
    document.addEventListener('click', handleClickOutSide, true);
    return () => {
      document.removeEventListener('resize', handleClickOutSide, false);
      document.removeEventListener('unload', handleClick(0), false);
      document.removeEventListener('click', handleClickOutSide, true);
    }
  }, []);

  const mapTenantData = (contents) => {
    return contents?.map((content) => {
      const {
        networkStatus: iotNetworkStatus,
      } = getNetworkStatusValue(content?.propertyIoTNetwork?.opStatus);
      const {
        networkStatus: residentNetworkStatus
      } = getNetworkStatusValue(content?.residentPrivateNetwork?.opStatus);
      const {
        connectionStatus
      } = getNetworkConnectionValue(content)
      return ({
        ...content,
        address: content?.tenantAddressLine1 + ", " + content?.tenantAddressLine2,
        iotNetworkStatusDropdown: {
          value: content?.propertyIoTNetwork?.opStatus,
          label: iotNetworkStatus
        },
        residentNetworkStatusDropdown: {
          value: content?.residentPrivateNetwork?.opStatus,
          label: residentNetworkStatus
        },
        connectionStatusDropdown: {
          value: content?.connectionStatus,
          label: connectionStatus
        }
      })
    })

  }

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    // console.log('EVENT', name)
    // console.log('Val', value)

    setFilterOptions(oldData => ({
      ...oldData,
      [name]: value
    }))
  }

  const handleResize = () => {
    setDimensions({
      width: window.innerWidth
    });
  }

  // WIN-1603: Pagination is now purely client-side
  const handleClick = (currentPageId) => {
    setCurrentPage(parseInt(currentPageId));
    history.replace(`psp?page=${currentPageId}`)
  }

  const handleClickOutSide = (event) => {
    if (
      dropDownRef.current &&
      !dropDownRef.current.contains(event.target)
    ) {
      setShowDropDown(false);
    }
  };

  const confirmModalCancelHandler = () => {
    setConfirmModal(false)
  }

  const confirmModalConfirmHandler = () => {


    if (!!props.tenantDetails) {
      if (props.tenantDetails.isSpectrumReady) {
        props.resumeSpecReadyServiceTenantAccount(selectedTenant.routerMac, props.propertyManager.accountNumber);
      } else {
        const payload = {
          tenantName: `${selectedTenant.tenantFirstName} ${selectedTenant.tenantLastName}`,
          propertyName: props.propertyManager.businessName,
          tenantAccountNumber: selectedTenant.accountNumber,
          tenantAccountDivisionId: selectedTenant.divisionId,
          tenantAccountRouterMac: selectedTenant.routerMac,
          bulkMasterAccount: null,
        }
        props.restoreServiceTenantAccount(selectedTenant.routerMac, props.propertyManager.accountNumber, payload);
      }

      setConfirmModal(false);
    }
  }

  const filterDuplicatesDropDown = (arr, key) => {
    const copy_set = new Set();
    return arr.filter(item => {
      const keyValue = item?.[key]?.value || item?.[key];
      if (copy_set.has(keyValue)) {
        return false;
      }
      copy_set.add(keyValue);
      return true;
    });
  }

  const fetchAndOpenRestoreModal = async (element) => {
    let tenantAddressLine1 = element.tenantAddressLine1 || sessionStorage.getItem('tenantAddressLine1') || '';
    let tenantAddressLine2 = element.tenantAddressLine2 || sessionStorage.getItem('tenantAddressLine2') || '';
    if (!tenantAddressLine1 && element.accountNumber) {
      try {
        const masterAcct = sessionStorage.getItem('masterAcct');
        const response = await api.get(`/psp/bulkMasterAccount/${masterAcct}/accountNumber/${element.accountNumber}`);
        tenantAddressLine1 = response?.data?.tenantAddressLine1 || '';
        tenantAddressLine2 = response?.data?.tenantAddressLine2 || '';
      } catch (e) {
        console.error('Failed to fetch tenant address', e);
      }
    }
    const enrichedElement = { ...element, tenantAddressLine1, tenantAddressLine2 };
    setSelectedTenant(enrichedElement);
    openModal(enrichedElement);
  };

  const openModal = (TenantDetails) => {
    const modalContent = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <span style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}>You are Restoring service to</span>
        <span style={{ fontWeight: 'normal', textAlign: 'center' }}>
          {`${TenantDetails.tenantAddressLine1}, ${TenantDetails.tenantAddressLine2}`}
        </span>
        <h4 style={{ textAlign: 'center' }}>
          Are you sure you want to proceed?
        </h4>
      </div>
    );
    setConfirmModal(true);
    setResult(modalContent);
  }

  const displayTenantUI = (element) => {
    props.onGetTenant(sessionStorage.getItem('masterAcct'), element.accountNumber, undefined, currentPage, element);
  }

  const getNetworkStatusValue = (currentStatus) => {
    let networkStatus = currentStatus;
    let networkStatusColor = '#d6312b';
    if (currentStatus === 'INACTIVE') {
      networkStatus = 'Inactive';
      networkStatusColor = '#6F777E';
    } else if (currentStatus === 'CONNECTED') {
      networkStatus = 'Connected';
      networkStatusColor = 'green';
    } else if (currentStatus === 'DISCONNECTED') {
      networkStatus = 'Offline';
      networkStatusColor = '#d6312b';
    }
    return {
      networkStatus,
      networkStatusColor
    }
  }

  const getNetworkConnectionValue = (element) => {
    let connectionStatus;
    let connectionStatusColor;
    if (element.connectionStatus === 'INACTIVE') {
      connectionStatus = 'Inactive';
      connectionStatusColor = '#6F777E';
    } else if (element.connectionStatus === 'CONNECTED') {
      connectionStatus = 'Connected';
      connectionStatusColor = 'green';
    } else if (element.connectionStatus === 'SUSPENDED') {
      connectionStatus = 'Suspended';
      connectionStatusColor = '#d6312b';
    } else if (element.connectionStatus === 'OFFLINE_SUSPENDED') {
      connectionStatus = 'Offline & Suspended';
      connectionStatusColor = '#d6312b';
    } else if (element.connectionStatus === 'OFFLINE') {
      connectionStatus = 'Offline';
      connectionStatusColor = '#d6312b';
    }
    else if (element.connectionStatus === 'NA') {
      connectionStatus = 'Offline';
      connectionStatusColor = '#6F777E';
    } else {
      connectionStatus = element.connectionStatus;
      connectionStatusColor = '#d6312b';
    }
    return {
      connectionStatus,
      connectionStatusColor
    }
  }

  const getTenantDetails = (currentRecords, type) => {
    let tenantDetails;

    if (props.isLoading) {
      return (
        <tr>
          <td colSpan="2"
            style={{ textAlign: 'center', color: 'grey', borderRight: 'none', padding: type === 'Desktop' ? '5rem' : '1.5rem' }}>
            <Spinner /></td>
        </tr>
      )
    }

    if ((currentRecords && currentRecords.length)) {
      tenantDetails = currentRecords.map((element) => {

        const tenantAddress = element.tenantAddressLine1 + ', ' + element.tenantAddressLine2;
        let connectionStatus;
        let connectionStatusColor;
        if (element.connectionStatus === 'INACTIVE') {
          connectionStatus = 'Inactive';
          connectionStatusColor = '#6F777E';
        } else if (element.connectionStatus === 'CONNECTED') {
          connectionStatus = 'Connected';
          connectionStatusColor = 'green';
        } else if (element.connectionStatus === 'SUSPENDED') {
          connectionStatus = 'Suspended';
          connectionStatusColor = '#d6312b';
        } else if (element.connectionStatus === 'OFFLINE_SUSPENDED') {
          connectionStatus = 'Offline & Suspended';
          connectionStatusColor = '#d6312b';
        } else if (element.connectionStatus === 'OFFLINE') {
          connectionStatus = 'Offline';
          connectionStatusColor = '#d6312b';
        }
        else if (element.connectionStatus === 'NA') {
          connectionStatus = 'Offline';
          connectionStatusColor = '#6F777E';
        } else {
          connectionStatus = element.connectionStatus;
          connectionStatusColor = '#d6312b';
        }
        const {
          networkStatus: iotNetworkStatus,
          networkStatusColor: iotNetworkStatusColor
        } = getNetworkStatusValue(element?.propertyIoTNetwork?.opStatus)
        const {
          networkStatus: residentNetworkStatus,
          networkStatusColor: residentNetworkStatusColor
        } = getNetworkStatusValue(element?.residentPrivateNetwork?.opStatus)
        // console.log("connection statuss:::",connectionStatus)
        // const showRestoreService = props.tenantDetails.isSpectrumReady ?
        //  ['CONNECTED', 'OFFLINE', 'UNKNOWN'].indexOf(element?.residentPrivateNetwork?.opStatus) === -1 : false :
        //   ['CONNECTED', 'OFFLINE', 'UNKNOWN'].indexOf(element?.connectionStatus) === -1 : false;

        // const showRestoreService = connectionStatus.includes('Suspen') && props.tenantDetails.isSpectrumReady

        const showRestoreService = props.tenantDetails.isSpectrumReady ?
        element?.residentPrivateNetwork?.opStatus
          ? ['CONNECTED', 'OFFLINE', 'UNKNOWN'].indexOf(element?.residentPrivateNetwork?.opStatus) === -1
          : false
        : element?.connectionStatus
          ? ['CONNECTED', 'OFFLINE', 'UNKNOWN'].indexOf(element?.connectionStatus) === -1
          : false;


        // console.log('props.tenantDetails ::::', showRestoreService)
        const showEllipse = element.connectionStatus ? props.tenantDetails.isSpectrumReady ? element.connectionStatus.toUpperCase() !== 'INACTIVE' && 
                            element.connectionStatus.toUpperCase() !== 'NA' && 
                            element.connectionStatus.toUpperCase() !== 'UNKNOWN' && 
                            element?.residentPrivateNetwork?.opStatus !== "UNKNOWN" && 
                            element?.occupancyStatus !== 'VACANT' && element?.occupancyStatus === "OCCUPIED"  : element.connectionStatus.toUpperCase() !== 'INACTIVE' && 
                            element.connectionStatus.toUpperCase() !== 'UNKNOWN' && element.connectionStatus !== 'NA' : false
        const isNonBulk = props.tenantDetails?.accountClassification && props.tenantDetails?.accountClassification === "MDUNONBULK" ? true : false;

        if (type === 'Desktop') {

          return (
            <tr className={classes.pspTrTag}>
              <td>
                <span style={{ fontColor: '#6F777E' }}> {tenantAddress} </span>
              </td>

              {props.tenantDetails.isSpectrumReady && <td>

                <div className={classes.opStatus} style={element?.occupancyStatus === "OCCUPIED" ? { backgroundColor: "green" } : { backgroundColor: "gray" }} >
                  {element?.occupancyStatus === "OCCUPIED" ? <FaUserCheck /> : userXMark}
                  {formatStatus(element?.occupancyStatus)}
                </div>
              </td>}

              <td >
                <div className={classes.pspAccountStatus} >
                  <div className={classes.statusDot} style={{ backgroundColor: connectionStatusColor }}></div>
                  <div style={{ width: '10px' }} />
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {formatStatus(connectionStatus)}
                  </div>
                </div>
              </td>
              {(props.tenantDetails.isSpectrumReady && props.iotConfigured) && <td>
                <div className={classes.pspAccountStatus}>
                  <div className={classes.statusDot} style={{ backgroundColor: iotNetworkStatusColor }}></div>
                  <div style={{ width: '10px' }} />
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {formatStatus(iotNetworkStatus)}
                  </div>
                </div>
              </td>}
              {props.tenantDetails.isSpectrumReady && <td>
                <div className={classes.pspAccountStatus}>
                  <div className={classes.statusDot} style={{ backgroundColor: residentNetworkStatusColor }}></div>
                  <div style={{ width: '10px' }} />
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {formatStatus(residentNetworkStatus)}

                  </div>
                </div>
              </td>}

              <td className={classes.dropDownContentTag} style={{ color:'#0000EE'}}>
                {

                }
                 {
                  (showEllipse) &&

                <div style={{ display: 'flex', alignItems:'center', float:'right'}}>
                {showRestoreService   && !isNonBulk &&
                  <div style={{ marginRight: '30px', textAlign: 'center'}}>
                    <Tooltip
                      content={
                        <div style={{
                          background: 'white',
                          id:'PSPRestore',
                          padding: 4,
                          maxWidth: '350px',
                          boxShadow: '1px 1px 3px 0px rgba(0,0,0,0.75)',
                        }}>
                          Restore Service
                        </div>
                      }
                      direction="down"
                      tagName="div"
                      className="target"
                      arrow={false}
                    >

                        <FaHouseSignal
                          id='PSPRestore'
                          color={'#0000EE'}
                          fontSize={'20px'}
                          onClick={() => fetchAndOpenRestoreModal(element)}
                          title="Restore Service"
                        />

                    </Tooltip>
                    <div id='PSPRestore'>Restore Service</div>
                  </div>
        }
                  <div style={{ textAlign: 'center'}}>
                    <Tooltip
                      content={
                        <div style={{
                          background: 'white',
                          padding: 4,
                          maxWidth: '350px',
                          boxShadow: '1px 1px 3px 0px rgba(0,0,0,0.75)',
                        }}>
                          Open In WIN
                        </div>
                      }
                      direction="down"
                      tagName="div"
                      className="target"
                      arrow={false}
                    >

                        <FaExternalLinkAlt
                          id='PSPExternalLink'
                          color={'#0000EE'}
                          fontSize={'20px'}
                          onClick={() => { displayTenantUI(element) }}
                          // onMouseOver={({target})=>target.style.color="white"}
                          // onMouseOut={({target})=>target.style.color="black"}
                          title="Open In WIN"
                        />

                    </Tooltip>
                    <div id ='PSPWIN'>WIN</div>
                  </div>
                </div>
                }
              </td>
            </tr>);
        } else {

          return (
            <tr className={classes.pspTrTag}>
              <td >
                <div className={classes.mobileTenantDetails}>
                  <span style={{ color: 'gray' }}> <b>Address:</b> {tenantAddress} </span>
                    {
                    /* {(showEllipse && !isNonBulk) && */}
                      <div className={classes.dropDownContentTag}>
                        <React.Fragment>
                          {showDropDown && selectedTenant.accountNumber === element.accountNumber &&
                            <div className={classes.dropDownContent} ref={dropDownRef}>
                              <span onClick={() => displayTenantUI(element)}

                                className={classes.dropDownOption}
                              >
                                View Unit Details
                              </span>
                              {['CONNECTED', 'OFFLINE'].indexOf(element.connectionStatus) === -1 &&
                                <React.Fragment>
                                  <div className={classes.spacer} />
                                  <span className={classes.dropDownOption} onClick={() => fetchAndOpenRestoreModal(element)}>Restore Service</span>
                                </React.Fragment>
                              }

                            </div>
                          }
                        </React.Fragment>
                      </div>
                    {/* } */}
                </div>
                <div className={classes.pspAccountStatus} ></div>
                <div className={classes.opStatus} style={{ backgroundColor: element?.occupancyStatus === "OCCUPIED" ? "green" : "gray", justifyContent: "center", marginBottom: "0.5rem" }} >
                  {element?.occupancyStatus === "OCCUPIED" ? <FaUserCheck /> : userXMark}
                  {element?.occupancyStatus || "N/A"}
                </div>
                <div className={classes.pspAccountStatus} style={{ marginBottom: "0.5rem" }} >
                  <b style={{ marginRight: "1rem" }}>Router Status:</b>
                  <div className={classes.statusDot} style={{ backgroundColor: connectionStatusColor }}></div>
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {connectionStatus}
                  </div>
                </div>
                <div className={classes.pspAccountStatus} style={{ marginBottom: "0.5rem" }}>
                  <b style={{ marginRight: "1rem" }}>IOT Network:</b>
                  <div className={classes.statusDot} style={{ backgroundColor: iotNetworkStatusColor }}></div>
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {iotNetworkStatus || "N/A"}
                  </div>
                </div>
                <div className={classes.pspAccountStatus} style={{ marginBottom: "0.5rem" }}>
                  <b style={{ marginRight: "1rem" }}>Resident Network:</b>
                  <div className={classes.statusDot} style={{ backgroundColor: residentNetworkStatusColor }}></div>
                  <div
                    id="pspAccountStatus"
                    style={{ color: 'gray' }}
                  >
                    {residentNetworkStatus || "N/A"}

                  </div>
                </div>
                <div  className={classes.pspAccountStatus}>
                {
                  <React.Fragment>

                    <Tooltip
                      content={
                        <div style={{
                          background: 'white',
                          padding: 4,
                          maxWidth: '350px',
                          boxShadow: '1px 1px 3px 0px rgba(0,0,0,0.75)',
                        }}>
                          Open In WIN
                        </div>
                      }
                      direction="down"
                      tagName="div"
                      className="target"
                      arrow={false}
                    >
                      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color:'#0000EE' }}>
                        <FaExternalLinkAlt
                          id='PSPExternalLink'
                          color={'#0000EE'}
                          fontSize={'20px'}
                          onClick={() => { displayTenantUI(element) }}
                          // onMouseOver={({target})=>target.style.color="white"}
                          // onMouseOut={({target})=>target.style.color="black"}
                          title="Open In WIN"
                        />
                        <span id ='PSPWIN'>WIN</span>
                      </Box>
                    </Tooltip>
                  </React.Fragment>
                }
                </div>

              </td>
            </tr>
          );
        }

      });
    } else {
      tenantDetails = (
        <tr>
          <td colSpan="5" style={{ textAlign: 'center', color: 'grey', borderRight: 'none', padding: type === 'Desktop' ? '5rem' : '1.5rem' }}>
            No Results Found Please Try Your Search Again.
          </td>
        </tr>
      );
    }
    return tenantDetails;
  }

  const findTargetView = () => {
    if (window.innerWidth <= 760) {
      return 'Mobile';
    } else {
      return 'Desktop';
    }
  };
  // WIN-1603: Filtering is now purely client-side — no server calls
  const fetchTenantData = (event, name) => {
    const { value } = event;
    setFilterOptions({
      address: "",
      occupancyStatus: "",
      routerStatus: "",
      iotNetwork: "",
      residentNetwork: "",
      [name]: value
    });
    history.replace('psp?page=0');
  }
  let renderPageNumbers;
  let targetView;
  let searchClear;
  const isAccountClassificationNull = !props.tenantDetails?.accountClassification;
  const isContentEmpty = !props.tenantDetails?.content || !props.tenantDetails.content.length;

  if (props.tenantDetails && isAccountClassificationNull && isContentEmpty) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '20px 28px' }}>
        <div style={{ textAlign: 'center', color: 'grey', padding: '3rem' }}>
          No Results Found Please Try Your Search Again.
        </div>
      </div>
    );
  }

  if (props.tenantDetails && isContentEmpty) {
    return (
      <div className={classes.pspViewTableWrapper}>
        <div style={{ position: 'relative', flexDirection: 'row', alignItems: 'center' }}>
          <div>
            <table className={classes.pspViewTable} cellPadding="0" cellSpacing="0">
              <thead>
                <tr className={classes.pspTrTag}>
                  <th style={{ width: '50%' }}>
                    Address
                    {/* <div>
                          <input value={filterOptions.address} type="text" name="address" className={classes.PSPSearchBox}
                        placeholder="Search Address or unit" onChange={handleFilterChange} />
                        </div> */}
                    <div className={classes.networkSearch} ref={dropDownRef} >
                      <div style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <input
                          id='PSPSearchBox'
                          className={classes.PSPSearchBox}
                          type="text"
                          placeholder="Search Unit or Address #"
                          value={searchValue}
                          onChange={(event) => {
                            setSearchValue(event.target.value)
                          }}
                          aria-label={"PSP Search Box"}
                        />
                        <span id='PSPSearch' onClick={
                          () => {
                            // WIN-1603: search is reactive, no server call needed
                          }}
                          style={{ cursor: 'pointer', backgroundColor: '#0073d1', color: '#fff', padding: 9, marginTop: 10 }}> <FaSearch /> </span>
                        <Box>
                          <span onClick={() => {
                            setSearchValue('');
                            setFilterOptions({
                              address: "",
                              occupancyStatus: "",
                              routerStatus: "",
                              iotNetwork: "",
                              residentNetwork: ""
                            });
                          }} id='PSPClear' style={{ marginLeft: 10, cursor: 'pointer', backgroundColor: '#0073d1', color: '#fff', padding: 9, marginTop: 10 }}> Clear X </span>
                          <span className={classes.showMobile} onClick={() => {
                            setSearchValue('');
                          }} style={{ marginLeft: 10, cursor: 'pointer', backgroundColor: '#0073d1', color: '#fff', padding: 9, marginTop: 10 }}> X </span>
                        </Box>

                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <div style={{ textAlign: 'center', color: 'grey', borderRight: 'none', padding: '9.5rem' }}>
                No Results Found Please Try Your Search Again.
              </div>
            </table>
          </div>
        </div>
      </div>
    );
  }


  if (props.tenantDetails && props.tenantDetails.content && props.tenantDetails.content.length) {
    targetView = findTargetView();
    const totalElements = filteredData.length;
    const totalPages = Math.ceil(totalElements / itemsPerPage);
    if (totalPages >= 1) {
      renderPageNumbers = (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{`Showing ${currentPage * itemsPerPage + 1}-${Math.min((currentPage + 1) * itemsPerPage, totalElements)} of ${totalElements}`}</span>
          <ReactPaginate
            previousLabel={<MdKeyboardArrowLeft size="25px" />}
            nextLabel={<MdKeyboardArrowRight size="25px" />}
            breakLabel={'...'}
            breakClassName={'break-me'}
            pageCount={totalPages}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            forcePage={currentPage}
            onPageChange={(value) => { handleClick(value.selected) }}
            containerClassName={classes.pagination}
            activeClassName={classes.paginationactive}
            disabledClassName={classes.paginationDisable}
          />
        </div>
      )
    }
  }
  const hasActiveFilters = searchValue?.trim() || filterOptions?.occupancyStatus ||
    filterOptions?.routerStatus || filterOptions?.iotNetwork || filterOptions?.residentNetwork;

  if (hasActiveFilters) {
    searchClear = (
      <Box>
        <span onClick={() => {
          setSearchValue('');
          setFilterOptions({
            address: "",
            occupancyStatus: "",
            routerStatus: "",
            iotNetwork: "",
            residentNetwork: ""
          });
        }} id='PSPClear' style={{ marginLeft: 10, cursor: 'pointer', backgroundColor: '#0073d1', color: '#fff', padding: 9, marginTop: 10 }}> Clear X </span>
        <span className={classes.showMobile} onClick={() => {
          setSearchValue('');
          setFilterOptions({
            address: "",
            occupancyStatus: "",
            routerStatus: "",
            iotNetwork: "",
            residentNetwork: ""
          });
        }} style={{ marginLeft: 10, cursor: 'pointer', backgroundColor: '#0073d1', color: '#fff', padding: 9, marginTop: 10 }}> X </span>
      </Box>
    )
  }

  // console.log('filterOptions ::::', filterOptions);

  const isMduBulkNotReady = props.tenantDetails?.accountClassification === "MDUBULK" && !props.tenantDetails?.isSpectrumReady;
  const tableWrapperStyle = isMduBulkNotReady ? { border: '1px solid #e0e0e0', borderRadius: '4px', margin: 0 } : {};

  return (
    <Box>
      <div className={classes.pspViewTableWrapper} style={tableWrapperStyle}>
        {
          targetView === 'Desktop' || targetView === 'Mobile'
            ? (
              <div style={{ overflowX: 'auto', minHeight: '40vh' }}>
                <table className={classes.pspViewTable} cellPadding="0" cellSpacing="0">
                  <thead>
                    <tr className={classes.pspTrTag}>
                      <th style={{ width: '30%' }}>
                        Address
                        {/* <div>
                          <input value={filterOptions.address} type="text" name="address" className={classes.PSPSearchBox}
                        placeholder="Search Address or unit" onChange={handleFilterChange} />
                        </div> */}
                        <div className={classes.networkSearch} ref={dropDownRef} >
                          <div style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <input
                              id='PSPSearchBox'
                              className={classes.PSPSearchBox}
                              type="text"
                              placeholder="Search Unit or Address #"
                              value={searchValue}
                              onChange={(event) => {
                                setSearchValue(event.target.value)
                              }}
                              aria-label={"PSP Search Box"}
                            />
                            {searchClear}

                          </div>
                        </div>
                      </th>
                      {props.tenantDetails.isSpectrumReady && <th style={{ width: '15%' }}>
                        Occupancy
                        <div>
                          <SelectDropdown
                            height={'30px'}
                            width={'150px'}
                            name="occupancyStatus"
                            placeholder='Select Status'
                            options={occupancyOptions}
                            value={occupancyOptions.filter(option => option.value == filterOptions.occupancyStatus)}
                            isSearchable={false}
                            onChange={(e) => {
                              fetchTenantData(e, "occupancyStatus")}
                            }
                          />
                        </div>
                      </th>}
                      <th style={{ width: '15%' }}>
                        Router Status
                        <div>
                          <SelectDropdown
                            height={'30px'}
                            width={'150px'}
                            name="routerStatus"
                            placeholder='Select Status'
                            value={routerStatusOptions.filter(option => option.value == filterOptions.routerStatus)}
                            options={routerStatusOptions}
                            isSearchable={false}
                            onChange={(e) => {
                              fetchTenantData(e, "routerStatus")}
                            }
                          />
                        </div>
                      </th>
                      {(props.tenantDetails.isSpectrumReady && props.iotConfigured) && <th style={{ width: '15' }}>
                        IOT Network
                        <div>
                          <SelectDropdown
                            height={'30px'}
                            width={'150px'}
                            name="iotNetwork"
                            placeholder='Select Status'
                            value={iotNetworkOptions.filter(option => option.value == filterOptions.iotNetwork)}
                            options={iotNetworkOptions}
                            isSearchable={false}
                            onChange={(e) => {
                              fetchTenantData(e, "iotNetwork")}
                            }
                          />
                        </div>
                      </th>}
                      {props.tenantDetails.isSpectrumReady && <th style={{ width: '15' }}>
                        Resident Network
                        <div>
                          <SelectDropdown
                            height={'30px'}
                            width={'150px'}
                            name="residentNetwork"
                            placeholder='Select Status'
                            value={residentNetworkOptions.filter(option => option.value == filterOptions.residentNetwork)}
                            options={residentNetworkOptions}
                            isSearchable={false}
                            onChange={(e) => {
                              fetchTenantData(e, "residentNetwork")}
                            }
                          />
                        </div>
                      </th>}
                      <th colSpan="1" style={{ width: props.tenantDetails.isSpectrumReady ? "15%" : "inherit" }}>{""}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTenantDetails(filteredData.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage), 'Desktop')}
                  </tbody>
                </table>
                <Modal
                  deviceGroupConfirmation={'true'}
                  show={confirmModal}
                  modalClosed={confirmModalCancelHandler}
                >
                  <Confirm
                    deviceGroupConfirmation
                    result={result}
                    confirm={confirmModalConfirmHandler}
                    cancel={confirmModalCancelHandler}
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Proceed"
                    hideActionText={true}
                  />
                </Modal>
              </div>
            )
            : (null)
        }

        <Modal show={props.authError}>
          <Error id={'StartAppError'} />
        </Modal>
      </div>

      <div className={classes.tenantDetailsPagination} style={isMduBulkNotReady ? { margin: 0 } : {}}>
        <span>
          {renderPageNumbers}
        </span>
      </div>
    </Box>
  )

}

const mapStateToProps = state => ({
  authError: state.auth.error,
  propertyManager: state.propertyManager.propertyManager,
  restoreServiceSuccess: state.propertyManager.restoreServiceSuccess,
  restoreServiceError: state.propertyManager.restoreServiceError
})

const mapDispatchToProps = dispatch => ({
  restoreServiceTenantAccount: (routerMac, masterAccNum, cmsPayload) => dispatch(actions.restoreServiceTenantAccount(routerMac, masterAccNum, cmsPayload)),
  resumeSpecReadyServiceTenantAccount: (routerMac, masterAccNum) => dispatch(actions.resumeSpecReadyServiceTenantAccount(routerMac, masterAccNum))
})

export default connect(mapStateToProps, mapDispatchToProps)(withErrorHandler(PspView, api));


